/**
 * NetworkDiagnosticsPanel.tsx
 *
 * Renders the decoded DIAG (L3-trace) capture for a test execution — the
 * UI-ready output of the edge's diag_summary.py, written by central-server onto
 * `executions/{id}.diagSummary`. The point of the whole DIAG feature: a user
 * learns *why* a test behaved the way it did (reject cause, signal quality over
 * the run, neighbor cells, 5G beams) WITHOUT opening the raw `l3_trace` pcap.
 *
 * Shared across surfaces: the Tests "Signaling" tab and the Reports RunDetail
 * page. Every field is optional, so a capture-skipped or older run renders nothing.
 *
 * 5G (#250): the deep decode's `nr` block drives an EN-DC / SA state row and a
 * non-fatal NR reject line; the NR RF section handles NSA-idle windows (no NR
 * serving cell → the series is the strongest measured cell).
 */

import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { AlertTriangle, CheckCircle2, Radio, Signal, Download, Network, Layers, Phone, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import type { DiagSummary, DiagRfBand, DiagSignalingDecoded, DiagRejectCause, DiagNrSignaling } from '@simcheck/shared-types';

interface ArtifactLike {
  id: string;
  type: string;
  filename?: string;
  sizeBytes?: number;
}

export interface NetworkDiagnosticsPanelProps {
  diag?: DiagSummary | null;
  artifacts?: ArtifactLike[];
  onDownload?: (artifact: ArtifactLike) => void;
  compact?: boolean;
}

const RSRP_COLOR = '#3b82f6'; // blue
const RSRQ_COLOR = '#f59e0b'; // amber

function RfChart({ band, height = 180 }: { band: DiagRfBand; height?: number }) {
  if (!band.series || band.series.length === 0) return null;
  const data = band.series.map((s) => ({ seq: s.seq, RSRP: s.rsrp, RSRQ: s.rsrq }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="seq" tick={{ fontSize: 10 }} className="text-muted-foreground"
               label={{ value: 'sample', position: 'insideBottom', offset: -2, fontSize: 10 }} />
        <YAxis yAxisId="rsrp" tick={{ fontSize: 10 }} width={44}
               domain={['dataMin - 3', 'dataMax + 3']} />
        <YAxis yAxisId="rsrq" orientation="right" tick={{ fontSize: 10 }} width={36}
               domain={['dataMin - 2', 'dataMax + 2']} />
        <Tooltip contentStyle={{ fontSize: 12, background: 'hsl(var(--card))',
                                 border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line yAxisId="rsrp" type="monotone" dataKey="RSRP" stroke={RSRP_COLOR}
              dot={false} strokeWidth={2} name="RSRP (dBm)" isAnimationActive={false} />
        <Line yAxisId="rsrq" type="monotone" dataKey="RSRQ" stroke={RSRQ_COLOR}
              dot={false} strokeWidth={2} name="RSRQ (dB)" isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

/** Section whose heavy body (table/chart) is collapsed by default; the header — with
 *  its summary badges — stays visible as the toggle, so the run-details modal opens
 *  compact and the ladders/measurements expand on click. */
function CollapsibleSection({
  header, children, defaultOpen = false,
}: { header: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-2 text-left"
      >
        <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
        {header}
      </button>
      {open ? children : null}
    </div>
  );
}

function RfBandSection({ title, icon, band }: { title: string; icon: React.ReactNode; band: DiagRfBand }) {
  const r = band.rollup;
  return (
    <CollapsibleSection
      header={
        <>
          {icon}
          <span className="text-sm font-medium text-foreground">{title}</span>
          {r.servingPci === null ? (
            // NR, NSA-idle for the whole window: no NR serving cell; the series is
            // the strongest measured cell (`bestPci`).
            <Badge variant="outline" className="text-[10px]" title="The modem had no NR serving cell (EN-DC not active) during this window; measurements are of the strongest visible NR cell.">
              no NR serving cell (idle)
            </Badge>
          ) : r.servingPci !== undefined ? (
            <Badge variant="outline" className="text-[10px]">PCI {r.servingPci}</Badge>
          ) : null}
          {r.servingPci === null && r.bestPci !== undefined && (
            <Badge variant="outline" className="text-[10px]">best PCI {r.bestPci}</Badge>
          )}
          {r.earfcn !== undefined && (
            <Badge variant="outline" className="text-[10px]">EARFCN {r.earfcn}</Badge>
          )}
          {r.nrarfcn !== undefined && (
            <Badge variant="outline" className="text-[10px]">NR-ARFCN {r.nrarfcn}</Badge>
          )}
          {r.nrBand !== undefined && (
            <Badge variant="outline" className="text-[10px]">n{r.nrBand}</Badge>
          )}
        </>
      }
    >
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Stat label="RSRP avg" value={r.rsrp ? `${r.rsrp.avg} dBm` : undefined} />
        <Stat label="RSRP min/max" value={r.rsrp ? `${r.rsrp.min} / ${r.rsrp.max}` : undefined} />
        <Stat label="RSRQ avg" value={r.rsrq ? `${r.rsrq.avg} dB` : undefined} />
        <Stat label="Samples" value={r.samples} />
        <Stat label="Idle samples" value={r.samplesIdle} />
        <Stat label="Neighbors" value={r.neighborCount} />
      </div>
      <RfChart band={band} />
      {band.neighbors && band.neighbors.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Neighbor PCI</th>
                <th className="px-2 py-1 text-right font-medium">RSRP (dBm)</th>
                <th className="px-2 py-1 text-right font-medium">RSRQ (dB)</th>
              </tr>
            </thead>
            <tbody>
              {band.neighbors.map((n, i) => (
                <tr key={`${n.pci}-${i}`} className="border-t border-border">
                  <td className="px-2 py-1 text-foreground">{n.pci}</td>
                  <td className="px-2 py-1 text-right text-foreground">{n.rsrp}</td>
                  <td className="px-2 py-1 text-right text-foreground">{n.rsrq}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}

function layerColor(layer: string): string {
  const l = layer.toLowerCase();
  if (l.includes('5gs')) return 'text-fuchsia-600 dark:text-fuchsia-400'; // NAS-5GS before the generic NAS test
  if (l.includes('nas')) return 'text-amber-600 dark:text-amber-400';
  if (l.includes('nr')) return 'text-violet-600 dark:text-violet-400';
  if (l.includes('rrc')) return 'text-blue-600 dark:text-blue-400';
  if (l.includes('sip')) return 'text-emerald-600 dark:text-emerald-400';
  return 'text-muted-foreground';
}

/** The authoritative RRC + NAS signaling ladder (server-side tshark decode). */
function SignalingLadder({ dec }: { dec: DiagSignalingDecoded }) {
  const tl = dec.timeline || [];
  if (!tl.length) return null;
  const c = dec.counts || {};
  return (
    <CollapsibleSection
      header={
        <>
          <Layers className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Signaling ladder</span>
        {c.lteRrc ? <Badge variant="outline" className="text-[10px]">LTE-RRC {c.lteRrc}</Badge> : null}
        {c.nrRrc ? (
          <Badge variant="outline" className="text-[10px]" title={dec.nr?.broadcastFrames ? `${dec.nr.broadcastFrames} of these are NR MIB/SIB1 broadcast frames (collapsed in the ladder)` : undefined}>
            NR-RRC {c.nrRrc}{dec.nr?.broadcastFrames ? ` (${dec.nr.broadcastFrames} bcast)` : ''}
          </Badge>
        ) : null}
        {c.nasEps ? <Badge variant="outline" className="text-[10px]">NAS-EPS {c.nasEps}</Badge> : null}
        {c.nas5gs ? <Badge variant="outline" className="text-[10px]">NAS-5GS {c.nas5gs}</Badge> : null}
          {dec.totalSignalingFrames ? (
            <span className="text-[10px] text-muted-foreground">{dec.totalSignalingFrames} frames</span>
          ) : null}
        </>
      }
    >
      <div className="max-h-64 overflow-y-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <tbody>
            {tl.map((e, i) => (
              <tr key={i} className="border-t border-border first:border-t-0">
                <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">{e.t.toFixed(2)}s</td>
                <td className={`whitespace-nowrap px-2 py-1 font-medium ${layerColor(e.layer)}`}>{e.layer}</td>
                <td className="px-2 py-1 text-foreground">{e.info}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}

/** IMS/SIP REGISTER/INVITE ladder — present only when a voice/IMS event was captured. */
/** IMS / VoLTE call decode — register/INVITE/connect/end milestones + media, from
 * the Qualcomm IMS-SIP + Call-Manager event stream (no full SIP bodies, but the
 * call flow + RTP). */
function ImsLadder({ ims }: { ims: NonNullable<DiagSignalingDecoded['ims']> }) {
  if (!ims || (!ims.ladder?.length && !ims.sipLadder?.length && !ims.rtp)) return null;
  const rtp = ims.rtp;
  const labelColor = (label: string) =>
    /connected|registered|established/i.test(label) ? 'text-emerald-600 dark:text-emerald-400'
      : /ended|reject|fail|terminated/i.test(label) ? 'text-amber-600 dark:text-amber-400'
        : 'text-foreground';
  // SIP status-aware colour: 2xx green, 4xx/5xx/6xx amber, 1xx/requests neutral.
  const sipColor = (status?: number | null) =>
    typeof status === 'number'
      ? (status >= 200 && status < 300 ? 'text-emerald-600 dark:text-emerald-400'
        : status >= 400 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground')
      : 'text-foreground';
  const sip = ims.sipLadder;
  return (
    <CollapsibleSection
      header={
        <>
          <Phone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <span className="text-sm font-medium text-foreground">IMS / VoLTE call</span>
        {ims.sipDecoded ? <Badge variant="secondary" className="text-[10px]" title="Real SIP message bodies decoded from the IMS SIP-message log (QXDM/QCAT-level)">SIP decoded</Badge> : null}
        {ims.registered ? <Badge variant="secondary" className="text-[10px]">IMS registered</Badge> : null}
        {ims.callConnected ? <Badge variant="secondary" className="text-[10px]">Connected</Badge> : null}
        {ims.codec ? <Badge variant="secondary" className="text-[10px]">{ims.codec}</Badge> : null}
        {typeof ims.setupMs === 'number' ? <Badge variant="outline" className="text-[10px]">setup {ims.setupMs} ms</Badge> : null}
        {typeof ims.durationMs === 'number' ? <Badge variant="outline" className="text-[10px]">call {(ims.durationMs / 1000).toFixed(1)} s</Badge> : null}
        {ims.sipMessages ? <Badge variant="outline" className="text-[10px]">{ims.sipMessages} SIP msgs</Badge> : null}
          {rtp && (rtp.recv || rtp.send) ? (
            <Badge variant="outline" className="text-[10px]" title="DIAG-logged RTP packets (throttled sample — lower bound)">RTP ↓{rtp.recv} ↑{rtp.send}</Badge>
          ) : null}
        </>
      }
    >
      {sip?.length ? (
        // Authoritative SIP request/response ladder (real message bodies).
        <div className="max-h-56 overflow-y-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <tbody>
              {sip.map((m, i) => (
                <tr key={i} className="border-t border-border first:border-t-0">
                  <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">{m.t.toFixed(2)}s</td>
                  <td className="w-4 px-1 py-1 text-center text-muted-foreground" title={m.dir === 'send' ? 'UE → network' : m.dir === 'recv' ? 'network → UE' : ''}>
                    {m.dir === 'send' ? '→' : m.dir === 'recv' ? '←' : ''}
                  </td>
                  <td className={`px-2 py-1 font-medium ${sipColor(m.status)}`}>{m.label}</td>
                  <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">{m.cseq || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : ims.ladder?.length ? (
        <div className="max-h-56 overflow-y-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <tbody>
              {ims.ladder.map((e, i) => (
                <tr key={i} className="border-t border-border first:border-t-0">
                  <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">{e.t.toFixed(2)}s</td>
                  <td className={`px-2 py-1 font-medium ${labelColor(e.label)}`}>{e.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </CollapsibleSection>
  );
}

/** Non-fatal NAS rejects (attach succeeded; a secondary request — typically the
 * modem's automatic IMS-PDN on a data-only SIM — was refused). Collapsed like the
 * ladders so a routine refusal doesn't paint a healthy run red. */
function RejectsSection({ rejects }: { rejects: DiagRejectCause[] }) {
  if (!rejects.length) return null;
  const imsPdnHint = rejects.some((r) => r.domain === 'ESM' && r.cause === 33);
  return (
    <CollapsibleSection
      header={
        <>
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-foreground">Network rejects</span>
          {rejects.map((r, i) => (
            <Badge key={i} variant="outline" className="text-[10px]">{r.domain} #{r.cause}</Badge>
          ))}
          <span className="text-[10px] text-muted-foreground">non-fatal — attach succeeded</span>
        </>
      }
    >
      <div className="space-y-2">
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <tbody>
              {rejects.map((r, i) => (
                <tr key={i} className="border-t border-border first:border-t-0">
                  <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums text-muted-foreground">
                    {typeof r.t === 'number' ? `${r.t.toFixed(2)}s` : ''}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 font-medium text-amber-600 dark:text-amber-400">
                    {r.domain} #{r.cause}
                  </td>
                  <td className="px-2 py-1 text-foreground">{r.text}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.raw}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {imsPdnHint && (
          <p className="text-xs text-muted-foreground">
            ESM #33 after a successful attach is usually the modem's automatic IMS-PDN request being
            refused — expected on data-only SIMs; VoLTE is unavailable on this SIM, data is unaffected.
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}

/** DIAG-derived 5G state (EN-DC / 5G-SA attempts) + the non-fatal NR reject.
 *  Distinct from the AT-derived `nsaState` tile in ConnectivitySnapshot: this
 *  is what the signalling shows, not whether user-plane bytes rode NR. */
function NrStateRow({ nr, fatalReject }: { nr: DiagNrSignaling; fatalReject: DiagRejectCause | null }) {
  const e = nr.endc;
  let badge: React.ReactNode = null;
  switch (nr.endcState) {
    case 'endc_configured':
      badge = (
        <Badge variant="secondary" className="text-[10px]" title="An LTE RRCConnectionReconfiguration added an NR secondary cell group (EN-DC / NSA active).">
          EN-DC configured · SCG added ×{e.scgAdded}{e.scgFailures ? ` · ${e.scgFailures} SCG failure${e.scgFailures > 1 ? 's' : ''}` : ''}
        </Badge>
      );
      break;
    case 'sa_registered':
      badge = <Badge variant="secondary" className="text-[10px]">5G-SA registered</Badge>;
      break;
    case 'sa_attempted_rejected':
      badge = (
        <Badge variant="outline" className="border-amber-500/50 text-[10px] text-amber-600 dark:text-amber-400" title="The modem attempted a 5G standalone registration and the network rejected it; the run stayed on its LTE anchor.">
          5G-SA attempted ×{nr.sa.attempts} — rejected
        </Badge>
      );
      break;
    case 'endc_capable':
      badge = (
        <Badge variant="outline" className="text-[10px]" title="SIB2 upperLayerIndication: the LTE cell advertises EN-DC, but no NR secondary cell group was added in this window.">
          EN-DC capable (cell), no NR SCG in window
        </Badge>
      );
      break;
    case 'lte_only':
      badge = <Badge variant="outline" className="text-[10px]">LTE only</Badge>;
      break;
    default:
      return null;
  }
  const nrReject = nr.rejectCause;
  const showReject = !!nrReject && !(fatalReject && fatalReject.domain === nrReject.domain && fatalReject.cause === nrReject.cause);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Radio className="h-4 w-4 text-violet-500" />
        <span className="text-muted-foreground">5G NR:</span>
        {badge}
        {e.b1Reports ? <Badge variant="outline" className="text-[10px]">{e.b1Reports} B1 report{e.b1Reports > 1 ? 's' : ''}</Badge> : null}
      </div>
      {showReject && (
        <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          NR reject: {nrReject.domain} #{nrReject.cause} — {nrReject.text} (non-fatal)
        </div>
      )}
    </div>
  );
}

export const NetworkDiagnosticsPanel: React.FC<NetworkDiagnosticsPanelProps> = ({
  diag, artifacts = [], onDownload, compact = false,
}) => {
  const pcaps = artifacts.filter((a) => a.type === 'l3_trace');
  const hasContent = !!(diag && (diag.rf || diag.signaling || diag.signalingDecoded)) || pcaps.length > 0;
  if (!hasContent) return null;

  const sig = diag?.signaling;
  const dec = diag?.signalingDecoded;
  // Prefer the server-side authoritative tshark decode; fall back to the edge's shallow parse.
  const reject = dec?.rejectCause || sig?.rejectCause;
  const outcome = dec?.outcome || sig?.outcome;
  const attached = outcome === 'attached';
  // The destructive banner is for a FAILED attach only. When the attach succeeded,
  // any rejects (new `rejects[]`, or a legacy stored `rejectCause` from before the
  // fatal/non-fatal split) are secondary-PDN refusals → the collapsed section below.
  const fatalReject = !attached ? reject : null;
  const nonFatalRejects: DiagRejectCause[] = attached
    ? (dec?.rejects?.length ? dec.rejects : reject ? [reject] : [])
    : [];
  const lte = diag?.rf?.lte;
  const nr = diag?.rf?.nr;
  // Did the server-side tshark decode actually yield something renderable? A
  // voice/attach run has a signalingDecoded ladder/IMS even when there is no
  // shallow rf/signaling parse — so "decoded summary not available" must NOT
  // be shown just because rf/signaling are absent (it would contradict the
  // ladder rendered right above it).
  const hasDecoded = !!(
    dec &&
    ((dec.timeline && dec.timeline.length > 0) ||
      dec.ims ||
      (dec.counts && Object.values(dec.counts).some((n) => (n ?? 0) > 0)))
  );

  const body = (
    <div className="space-y-4">
      {/* Outcome / reject cause */}
      {fatalReject ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {fatalReject.domain} reject — cause #{fatalReject.cause}
          </AlertTitle>
          <AlertDescription>{fatalReject.text}</AlertDescription>
        </Alert>
      ) : outcome === 'rejected' ? (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Network rejected the attach</AlertTitle>
          <AlertDescription>
            Reject signaling was seen but no standard cause code was decoded. See the raw capture.
          </AlertDescription>
        </Alert>
      ) : outcome === 'attached' ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          Attach / registration completed.
        </div>
      ) : null}

      {/* 5G NR state (EN-DC / SA) from the deep decode */}
      {dec?.nr && <NrStateRow nr={dec.nr} fatalReject={fatalReject || null} />}

      {/* IMS / SIP final (shallow edge parse) */}
      {sig?.sipFinal && (
        <div className="flex items-center gap-2 text-sm">
          <Network className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">IMS / SIP final:</span>
          <Badge variant={sig.sipFinal.code >= 400 ? 'destructive' : 'secondary'}>
            {sig.sipFinal.code} {sig.sipFinal.reason}
          </Badge>
        </div>
      )}

      {/* Non-fatal rejects (attach succeeded) — collapsed like the ladders */}
      {nonFatalRejects.length > 0 && <RejectsSection rejects={nonFatalRejects} />}

      {/* Signaling ladder + IMS ladder (server-side authoritative tshark decode) */}
      {dec && <SignalingLadder dec={dec} />}
      {dec?.ims && <ImsLadder ims={dec.ims} />}

      {/* RF measurements */}
      {lte && <RfBandSection title="LTE RF measurements" icon={<Signal className="h-4 w-4 text-blue-500" />} band={lte} />}
      {nr && <RfBandSection title="5G NR RF measurements" icon={<Radio className="h-4 w-4 text-violet-500" />} band={nr} />}

      {/* Raw capture download */}
      {pcaps.length > 0 && (
        <div className="space-y-1 border-t border-border pt-3">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Raw capture (engineers)</span>
          {pcaps.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onDownload?.(a)}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Download className="h-4 w-4" />
              {a.filename || 'l3_trace.pcap'} (open in Wireshark)
            </button>
          ))}
        </div>
      )}

      {/* Only a raw pcap, nothing decodable (e.g. a data run with no
          control-plane signaling in the window). Suppressed when the deep
          decode DID produce a ladder/IMS/counts. */}
      {!diag?.rf && !diag?.signaling && !hasDecoded && pcaps.length > 0 && (
        <p className="text-xs text-muted-foreground">
          DIAG capture attached; no decodable signaling in this run's window
          (expected for a steady data test — signaling appears on voice/attach runs).
        </p>
      )}
    </div>
  );

  if (compact) return body;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Signal className="h-4 w-4" /> Network Diagnostics
          <span className="text-xs font-normal text-muted-foreground">(decoded from DIAG / L3 trace)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default NetworkDiagnosticsPanel;
